import { useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Globe, HelpCircle, Shield, Lightbulb, Compass,
  CheckSquare, Handshake, BookOpen, Zap,
  ExternalLink,
} from "lucide-react";

const CARD_TYPES = {
  fact_check:  { icon: Globe,       label: "Fact Check",  dot: "bg-blue-500",    accent: "border-l-blue-400" },
  follow_up:   { icon: HelpCircle,  label: "Follow Up",   dot: "bg-violet-500",  accent: "border-l-violet-400" },
  defense:     { icon: Shield,      label: "Defense",      dot: "bg-amber-500",   accent: "border-l-amber-400" },
  example:     { icon: Lightbulb,   label: "Example",      dot: "bg-emerald-500", accent: "border-l-emerald-400" },
  nudge:       { icon: Compass,     label: "Agenda",       dot: "bg-red-500",     accent: "border-l-red-400" },
  action_item: { icon: CheckSquare, label: "Action Item",  dot: "bg-emerald-600", accent: "border-l-emerald-500" },
  objection:   { icon: Handshake,   label: "Negotiation",  dot: "bg-orange-500",  accent: "border-l-orange-400" },
  grounded:    { icon: BookOpen,    label: "Grounded",     dot: "bg-teal-500",    accent: "border-l-teal-400" },
};

const VERDICT_LABEL = {
  accurate:           { text: "Accurate",   cls: "text-emerald-600" },
  inaccurate:         { text: "Inaccurate", cls: "text-red-600" },
  partially_accurate: { text: "Partial",    cls: "text-amber-600" },
  answer:             { text: "Answer",     cls: "text-blue-600" },
};

function isSafeUrl(url) {
  if (!url || typeof url !== "string") return false;
  try { return /^https?:\/\//i.test(new URL(url).href); } catch { return false; }
}

function extractCardData(card) {
  const { card_type, content } = card;
  let response = null;
  let context = [];
  let sourceUrl = null;
  let verdict = null;
  let sourceChip = null;

  switch (card_type) {
    case "fact_check":
      verdict = VERDICT_LABEL[content.verdict];
      response = content.explanation;
      if (content.claim) context.push(`"${content.claim}"`);
      if (content.sources?.length > 0 && isSafeUrl(content.sources[0])) sourceUrl = content.sources[0];
      break;
    case "follow_up":
      response = content.question;
      if (content.rationale) context.push(content.rationale);
      else if (content.trigger) context.push(`Re: "${content.trigger}"`);
      break;
    case "defense":
      response = content.reframe || content.counter_points?.[0];
      if (content.challenge) context.push(`"${content.challenge}"`);
      if (content.counter_points?.length > 1) context.push(content.counter_points.slice(1).join(" · "));
      break;
    case "example":
      response = content.examples?.[0]?.example;
      if (content.concept) context.push(content.concept);
      if (content.examples?.[0]?.how_to_use) context.push(content.examples[0].how_to_use);
      break;
    case "nudge": {
      response = content.transition || content.uncovered_item;
      const parts = [];
      if (content.uncovered_item && content.transition) parts.push(content.uncovered_item);
      if (content.reason) parts.push(content.reason);
      if (parts.length) context.push(parts.join(" · "));
      break;
    }
    case "action_item":
      response = content.quote || `So just to confirm, ${content.owner || "they"} will have this by ${content.deadline || "the deadline"}?`;
      if (content.action) context.push(content.action);
      { const meta = [content.owner, content.deadline].filter(Boolean);
        if (meta.length) context.push(meta.join(" · ")); }
      break;
    case "objection":
      response = content.suggested_response || content.compromise;
      if (content.tension_source) context.push(content.tension_source);
      if (content.their_concern) context.push(content.their_concern);
      if (content.compromise && content.suggested_response) context.push(`Alt: "${content.compromise}"`);
      break;
    case "grounded":
      response = content.suggestion;
      if (content.evidence) context.push(content.evidence);
      if (content.trigger) context.push(`Re: "${content.trigger}"`);
      sourceChip = content.source_title || null;
      break;
    default:
      break;
  }

  return { response, context, sourceUrl, verdict, sourceChip };
}

function formatCardTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return null; }
}

const cardSpring = { type: "spring", stiffness: 420, damping: 32 };

function CompactCard({ card, isHero }) {
  const config = CARD_TYPES[card.card_type] || CARD_TYPES.follow_up;
  const { response, context, sourceUrl, verdict, sourceChip } = extractCardData(card);
  const isImmediate = card.urgency === "immediate";
  const timeLabel = formatCardTime(card.timestamp);

  if (!response) return null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 60, scale: 0.97 }}
      transition={cardSpring}
      className={`
        group bg-white rounded border border-border border-l-[3px] ${config.accent}
        transition-shadow duration-150
        ${isHero
          ? "px-3 py-2.5 shadow-[0_2px_20px_-2px_rgba(0,0,0,0.08)] border-foreground/[0.08]"
          : "px-2.5 py-2 shadow-sm"
        }
      `}
    >
      {/* Row 1: Type + Verdict + Time */}
      <div className="flex items-center gap-1.5 mb-1">
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${config.dot}`} />
        <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
          {config.label}
        </span>
        {verdict && (
          <>
            <span className="text-muted-foreground/25">·</span>
            <span className={`text-[10px] font-semibold ${verdict.cls}`}>{verdict.text}</span>
          </>
        )}
        {!verdict && isImmediate && (
          <>
            <span className="text-muted-foreground/25">·</span>
            <span className="text-[10px] font-semibold text-foreground/60">Now</span>
          </>
        )}
        {timeLabel && (
          <>
            <span className="text-muted-foreground/25">·</span>
            <span className="text-[10px] tabular-nums text-muted-foreground/40">{timeLabel}</span>
          </>
        )}
      </div>

      {/* Row 2: Response */}
      <p className={`leading-snug tracking-[-0.01em] text-foreground ${
        isHero
          ? "text-[13px] font-medium"
          : "text-xs"
      } ${isImmediate ? "font-medium" : ""}`}>
        "{response}"
      </p>

      {/* Row 3: Context + Source */}
      {(context.length > 0 || sourceUrl) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          {context.length > 0 && (
            <p className="text-[11px] text-foreground/55 leading-snug">
              {context.join(" · ")}
            </p>
          )}
          {sourceUrl && (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-[11px] text-blue-500 hover:text-blue-700 transition-colors shrink-0"
            >
              <ExternalLink className="w-2.5 h-2.5" />
              Source
            </a>
          )}
        </div>
      )}

      {/* Source chip for grounded cards */}
      {sourceChip && (
        <div className="mt-1 flex items-center">
          <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-md bg-teal-500/10 text-teal-700 font-medium">
            <BookOpen className="w-2.5 h-2.5" />
            from: {sourceChip}
          </span>
        </div>
      )}
    </motion.div>
  );
}

export default function PipCardsWidget({ cards = [] }) {
  const scrollRef = useRef(null);
  const prevCardCount = useRef(cards.length);

  useEffect(() => {
    if (cards.length > prevCardCount.current && scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
    prevCardCount.current = cards.length;
  }, [cards.length]);

  if (cards.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="relative mb-3">
          <div className="w-10 h-10 rounded-full bg-primary/[0.04] breathe" />
          <div className="w-7 h-7 rounded-full bg-primary/[0.06] breathe absolute top-1.5 left-1.5" style={{ animationDelay: "0.5s" }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <Zap className="w-4 h-4 text-primary/20" />
          </div>
        </div>
        <p className="text-[11px] text-foreground/50 font-medium">Listening...</p>
        <p className="text-[10px] text-muted-foreground/35 mt-0.5">Cards appear as the conversation unfolds</p>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="flex-1 min-h-0 overflow-y-auto custom-scrollbar space-y-1.5 pb-1.5"
    >
      <AnimatePresence initial={false}>
        {cards.map((card, i) => (
          <CompactCard key={card.card_id} card={card} isHero={i === 0} />
        ))}
      </AnimatePresence>
    </div>
  );
}
